import * as React from 'react';

import gql from 'graphql-tag';
import _ from 'lodash';

import waitForGraphQL from 'js/lib/waitForGraphQL';

import { BROWSE_ROOT_URL as BROWSE_ROOT } from 'bundles/browse/constants';
import LinksContainer from 'bundles/directory-graphql/components/LinksContainer';
import type {
  DirectoryDomainsAndSubdomainsQuery,
  DirectoryDomainsAndSubdomainsQuery_DomainsV1Resource_getAll_domains as Domain,
  DirectoryDomainsAndSubdomainsQuery_SubdomainsV1Resource_getAll_subdomains as Subdomain,
} from 'bundles/directory-graphql/components/__generated__/DirectoryDomainsAndSubdomainsQuery';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';

import _t from 'i18n!nls/directory-graphql';

type Props = {
  domains: Array<Domain | null> | undefined;
  subdomains: Array<Subdomain | null> | undefined;
};

const Topics = ({ domains, subdomains }: Props) => {
  if (!domains || domains.length === 0 || !subdomains) {
    return null;
  }

  const getCatalogLink = (domainSlug: string, subdomainId?: string) => {
    return subdomainId ? `${BROWSE_ROOT}/${domainSlug}/${subdomainId}` : `${BROWSE_ROOT}/${domainSlug}`;
  };

  const getDomainAndSubdomainLinks = () => {
    const domainsList: Record<string, { name: string; slug: string | null }> = {};
    const subdomainsIdList: Array<Array<string>> = [];
    const validDomains = domains.filter((domain): domain is Domain => !!domain);
    validDomains.forEach(({ id: domainId, name, slug, subdomainIds }) => {
      domainsList[domainId] = { name, slug };
      subdomainsIdList.push(subdomainIds);
    });
    const domainLinks = Object.keys(domainsList).map((domainId) => {
      const { slug, name } = domainsList[domainId];
      return {
        title: name,
        link: getCatalogLink(slug || domainId),
      };
    });

    const validSubdomainIds: string[] = _.flattenDeep(subdomainsIdList);
    const validSubdomains = subdomains.filter((subdomain): subdomain is Subdomain => {
      return !!(subdomain && validSubdomainIds.includes(subdomain.id));
    });

    const subdomainLinks = validSubdomains.map((subdomain) => {
      const { domainId, name } = subdomain;
      const domainName = domainsList[domainId].name;
      const domainForSubdomain = validDomains.find((d) => d.id === domainId);
      if (domainForSubdomain) {
        return {
          title: domainName + ' - ' + name,
          link: getCatalogLink(domainForSubdomain.slug || domainForSubdomain.id, subdomain.slug || subdomain.id),
        };
      }
      return {
        title: '',
        link: '',
      };
    });
    return _.sortBy(domainLinks.concat(subdomainLinks), 'title');
  };

  const titledLinks = getDomainAndSubdomainLinks();

  if (!titledLinks) {
    return null;
  } else {
    return (
      <div>
        <LinksContainer titledLinks={titledLinks} />
        <CourseraMetatags
          metaNameAndProperties={{
            title: _t('Topics Directory'),
            description: _t(
              'The Coursera Topics directory includes links to each topic page within our course catalog.'
            ),
            disableCrawlerIndexing: true,
            useFollowForDisablingIndexing: true,
          }}
        />
      </div>
    );
  }
};

export default waitForGraphQL<{}, DirectoryDomainsAndSubdomainsQuery, {}, Props>(
  gql`
    query DirectoryDomainsAndSubdomainsQuery {
      DomainsV1Resource {
        getAll {
          domains: elements {
            id
            name
            slug
            subdomainIds
          }
        }
      }
      SubdomainsV1Resource {
        getAll {
          subdomains: elements {
            id
            name
            domainId
            slug
          }
        }
      }
    }
  `,
  {
    props: ({ data }) => {
      const subdomains = data?.SubdomainsV1Resource?.getAll?.subdomains;
      const domains = data?.DomainsV1Resource?.getAll?.domains;

      return {
        subdomains,
        domains,
      };
    },
  }
)(Topics);
